import { useCallback, useEffect, useMemo, useState } from "react";
import { emitToast } from "../../../../integrations/tools/outlook/toast-bridge";
import { consumePendingInAppNav, subscribeInAppNav, type InAppNavTarget } from "../../../shared/inAppLinks";
import { CONNECT_M365_TOAST, DOCUMENT_OPEN_FAILED_TOAST } from "../../../shared/openInAppRecord";
import { InvalidDriveItemRefError, resolveDriveItem } from "../../files/services/driveItemRef";
import { ModuleHeader } from "../../../shared/components/ModuleHeader";
import { KnowledgeIcon } from "../../../shared/components/icons";
import {
  fetchItemPreviewUrl,
  type GraphSearchItem,
  type GraphSite,
} from "../../email/services/graph";
import { useSharePointSites } from "../../files/sharepoint/hooks/useSharePointSites";
import { useFolderBrowser } from "../../files/onedrive/hooks/useFolderBrowser";
import { FolderBrowser } from "../../files/components/FolderBrowser";
import { getTagStyle } from "../../files/components/fileRowMeta";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logEvent, logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import { useKnowledgeSearch } from "../hooks/useKnowledgeSearch";
import { useKnowledgeTags } from "../hooks/useKnowledgeTags";
import { KnowledgeSearchResults } from "./KnowledgeSearchResults";
import styles from "./KnowledgeModule.module.css";

interface KnowledgeModuleProps {
  readonly graphToken: string | null;
  readonly onBack?: () => void;
}

/** Outline (line-art) magnifier — tints with the surrounding text colour. */
function SearchIcon() {
  return (
    <svg
      className={styles.searchSvg}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function siteName(site: GraphSite): string {
  return site.displayName || site.name || "Untitled site";
}

/** Distinct, alphabetically-sorted tags across a list of tagged items. */
function distinctTags(items: ReadonlyArray<{ tags?: string[] }>): string[] {
  return [...new Set(items.flatMap((i) => i.tags ?? []))].sort((a, b) => a.localeCompare(b));
}

/** Keep only items carrying `tag`; `null` (the "All" chip) keeps everything. */
function byTag<T extends { tags?: string[] }>(items: T[], tag: string | null): T[] {
  return tag ? items.filter((i) => (i.tags ?? []).includes(tag)) : items;
}

function siteInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "SP";
  return trimmed
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .substring(0, 2)
    .toUpperCase();
}

/**
 * Knowledge Base. Empty search box → browse SharePoint the way the Files module
 * does: site collections, then drill into folders/files (tags shown on each
 * row). Type ≥2 characters → tenant-wide search across every site the user can
 * access, shown as a flat results list with tag chips; a tag-filter chip row
 * under the search box narrows the results. Files open read-only for every type.
 */
export function KnowledgeModule({ graphToken, onBack }: Readonly<KnowledgeModuleProps>) {
  const { refreshGraphToken } = useAuthState();
  const [selectedSite, setSelectedSite] = useState<GraphSite | null>(null);
  // A folder opened from search results, browsed in-app via its own drive.
  const [searchFolder, setSearchFolder] = useState<{ driveId: string; id: string; name: string } | null>(null);
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState<string | null>(null);
  // A tenant-wide search driven by a tag chip clicked on the site list (where
  // there are no local items to filter). Kept separate from `query` so the
  // search box stays EMPTY — clicking a chip must not type the tag into it.
  const [tagQuery, setTagQuery] = useState<string | null>(null);
  const q = query.trim().toLowerCase();

  // A non-trivial query — typed, or a tag chip's tenant search — switches from
  // drill-down browse to a flat, tenant-wide search. The visible input only ever
  // reflects `query`; `tagQuery` drives the search without showing in the box.
  const typedSearch = query.trim().length >= 2;
  const searchTerm = typedSearch ? query.trim() : (tagQuery ?? "");
  const isSearching = searchTerm.length >= 2;
  // Tags and metadata-column values live ONLY in SharePoint's free-text index —
  // never in the filename — so typing a tag name in the search box ("SOP") can
  // only match if the search opts into that index. A tag-chip search already
  // does. So a typed search opts in too, matching NAME/TITLE + tags/content.
  // (Trade-off: the free-text index also carries body text, so a typed search
  // can now also match on content, not just the name.)
  const search = useKnowledgeSearch(graphToken, searchTerm, true);

  // Site collections stay loaded for the whole session (one fetch on open) so a
  // typed search can also match them by name — the hook fetches once per token
  // and keeps the result, so this doesn't add calls when browsing.
  const sitesQuery = useSharePointSites(graphToken);

  // Site collections whose NAME matches a typed query — shown alongside the file
  // / folder hits so search covers collections too. Only for a typed query (a tag
  // search has an empty box, so `q` is empty and nothing matches).
  const matchedSites = useMemo(
    () => (q ? sitesQuery.sites.filter((s) => siteName(s).toLowerCase().includes(q)) : []),
    [q, sitesQuery.sites],
  );

  // Folder browser is idle until a site is selected (and not searching / not in
  // a search-opened folder).
  const drivePath = selectedSite ? `/sites/${selectedSite.id}/drive` : null;
  const browser = useFolderBrowser(
    selectedSite && !isSearching && !searchFolder ? graphToken : null,
    drivePath,
    selectedSite ? siteName(selectedSite) : "SharePoint",
  );

  // Folder opened from a search result — browsed in-app via its own drive,
  // starting inside the folder (rootParentId).
  const searchFolderPath = searchFolder ? `/drives/${searchFolder.driveId}` : null;
  const searchFolderBrowser = useFolderBrowser(
    searchFolder ? graphToken : null,
    searchFolderPath,
    searchFolder?.name ?? "Folder",
    searchFolder?.id ?? null,
  );

  // Tenant-wide tag vocabulary, loaded once when the module opens, so the filter
  // chips render on every page — including the site-collection list, whose items
  // carry no tags of their own.
  const tenantTags = useKnowledgeTags(graphToken);

  // Per-page tags: whichever list is on screen (search-opened folder, tenant
  // search results, or a browsed site library). Unioned with the tenant set so
  // the chip bar is the same everywhere, yet still covers a folder tag that the
  // tenant sample happened to miss.
  const currentTags = useMemo(() => {
    if (searchFolder) return distinctTags(searchFolderBrowser.items);
    if (isSearching) return distinctTags(search.results);
    if (selectedSite) return distinctTags(browser.items);
    return [];
  }, [searchFolder, searchFolderBrowser.items, isSearching, search.results, selectedSite, browser.items]);

  const filterTags = useMemo(
    () => [...new Set([...tenantTags, ...currentTags])].sort((a, b) => a.localeCompare(b)),
    [tenantTags, currentTags],
  );

  const onQueryChange = useCallback((value: string) => {
    setQuery(value);
    setActiveTag(null); // a new query invalidates the previous tag selection
    setTagQuery(null); // …and exits any tag-driven search
    setSearchFolder(null); // …and exits a folder opened from search results
  }, []);

  // The "All" chip is a full reset to the root site-collection view: from any
  // depth — a deep browsed folder, or a folder opened from search results — it
  // drops the whole navigation stack (query, tag filter, tag search, search
  // folder and selected site) so the user lands back on the site-collection list.
  const resetToRoot = useCallback(() => {
    setQuery("");
    setActiveTag(null);
    setTagQuery(null);
    setSearchFolder(null);
    setSelectedSite(null);
  }, []);

  // Toggle a tag filter. Deselecting the active tag falls back to "All", which
  // means the root view — so it defers to resetToRoot for the same full reset the
  // "All" chip does. Selecting a tag narrows the current list via activeTag; on
  // the site-collection list there is nothing local to filter, so it instead runs
  // a tenant-wide search for that tag via tagQuery — which drives the search
  // WITHOUT writing the tag into the search box.
  const onTagFilterClick = useCallback(
    (tag: string) => {
      if (activeTag === tag) {
        resetToRoot(); // deselect → back to "All" → root
        return;
      }
      setActiveTag(tag);
      if (!selectedSite && !searchFolder && !typedSearch) {
        setTagQuery(tag); // site-list tag click → tenant-wide search for the tag
      }
    },
    [activeTag, selectedSite, searchFolder, typedSearch, resetToRoot],
  );

  // Files open read-only for every type (AIVA-7093): a Graph preview URL is the
  // only reliably view-only surface. Fails closed if no preview is returned.
  // Resolves to whether a file actually opened.
  const openFileReadOnly = useCallback(
    async (itemDrivePath: string | null, item: { id: string; name: string }): Promise<boolean> => {
      if (!graphToken || !itemDrivePath) return false;
      const ext = (item.name.split(".").pop() ?? "").toLowerCase();
      logEvent("connector.download", "usage", {
        connectorSystemName: "sharepoint",
        fileType: ext || "unknown",
      });
      try {
        const previewUrl = await fetchItemPreviewUrl(graphToken, itemDrivePath, item.id, refreshGraphToken);
        // Only a real open counts — see the engage sibling. No preview link
        // means nothing opened.
        if (previewUrl) {
          window.open(previewUrl, "_blank", "noopener,noreferrer");
          logConnectorSuccess("sharepoint", "open-file", { fileType: ext || "unknown" });
          return true;
        }
      } catch (e) {
        // Fail closed — a failed preview must not open an editable fallback —
        // but still record why the open failed for the connector pivot.
        logConnectorFailure("sharepoint", e, "open-file", { fileType: ext || "unknown" });
      }
      return false;
    },
    [graphToken, refreshGraphToken],
  );

  // A Navigate-to-Knowledge record from an Actions-tab card (shared/inAppLinks).
  // The Search Results view searches for the record; any other view opens the
  // item it names — a folder in the in-app browser, a file read-only.
  const openRecord = useCallback(
    async (target: InAppNavTarget) => {
      const record = target.record;
      if (!record) return;
      if (target.view === "search_results") {
        onQueryChange(record);
        return;
      }
      if (!graphToken) {
        emitToast({ title: CONNECT_M365_TOAST, intent: "error" });
        return;
      }
      try {
        const item = await resolveDriveItem(graphToken, record, refreshGraphToken);
        if (!item.driveId) throw new InvalidDriveItemRefError();
        if (item.isFolder) {
          onQueryChange("");
          setSearchFolder({ driveId: item.driveId, id: item.id, name: item.name || "Folder" });
          return;
        }
        if (await openFileReadOnly(`/drives/${item.driveId}`, item)) return;
      } catch (e) {
        logConnectorFailure("sharepoint", e, "open-record");
      }
      emitToast({ title: DOCUMENT_OPEN_FAILED_TOAST, intent: "error" });
    },
    [graphToken, refreshGraphToken, onQueryChange, openFileReadOnly],
  );

  // Claim a record parked before this module mounted, and take later ones live.
  // Claiming in an effect (never a useState initializer) keeps StrictMode's
  // discarded render from eating the park — see DeckorateModule.
  useEffect(() => {
    const parked = consumePendingInAppNav("knowledge");
    if (parked) void openRecord(parked);
    return subscribeInAppNav((target) => {
      if (target.tab !== "knowledge" || !target.record) return;
      consumePendingInAppNav("knowledge");
      void openRecord(target);
    });
  }, [openRecord]);

  // Open a cross-tenant search hit: a folder drills into its contents in-app
  // (browsed via its own drive); a file opens read-only on its own drive.
  const openSearchResult = useCallback(
    (item: GraphSearchItem) => {
      if (item.isFolder) {
        if (item.driveId) {
          setSearchFolder({ driveId: item.driveId, id: item.id, name: item.name || "Folder" });
        }
        return;
      }
      if (item.driveId) void openFileReadOnly(`/drives/${item.driveId}`, item);
    },
    [openFileReadOnly],
  );

  // Open a site-collection search hit: clear the search and browse into the site.
  const openSiteResult = useCallback((site: GraphSite) => {
    setQuery("");
    setTagQuery(null);
    setActiveTag(null);
    setSearchFolder(null);
    setSelectedSite(site);
  }, []);

  const filteredSites = sitesQuery.sites.filter((s) => !q || siteName(s).toLowerCase().includes(q));

  return (
    <div className={styles.module}>
      <ModuleHeader
        title="Knowledge Base"
        subtitle="Results across every connected repository"
        icon={KnowledgeIcon}
        accent="knowledge"
        onBack={onBack}
      />

      <div className={styles.searchRow}>
        <span className={styles.searchIcon}>
          <SearchIcon />
        </span>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          aria-label="Search the knowledge base"
        />
        <button
          type="button"
          className={styles.clear}
          onClick={() => onQueryChange("")}
          disabled={!query && !tagQuery}
          aria-label="Clear search"
        >
          ✕
        </button>
      </div>

      {filterTags.length > 0 && (
        <fieldset className={styles.tagbar} aria-label="Filter by tag">
          <button
            type="button"
            className={`${styles.tagFilter} ${activeTag === null ? styles.tagFilterActive : ""}`}
            aria-pressed={activeTag === null}
            onClick={resetToRoot}
          >
            All
          </button>
          {filterTags.map((t) => {
            const active = activeTag === t;
            const c = getTagStyle(t, active);
            return (
              <button
                key={t}
                type="button"
                className={styles.tagFilter}
                style={{ ...c, borderColor: active ? c.backgroundColor : undefined }}
                aria-pressed={active}
                onClick={() => onTagFilterClick(t)}
              >
                {t}
              </button>
            );
          })}
        </fieldset>
      )}

      <div className={styles.body}>
        {!graphToken && <div className={styles.empty}>Sign in to browse the knowledge base.</div>}

        {graphToken && searchFolder && (
          <FolderBrowser
            items={searchFolderBrowser.loading ? [] : byTag(searchFolderBrowser.items, activeTag)}
            loading={searchFolderBrowser.loading}
            error={searchFolderBrowser.error}
            path={searchFolderBrowser.path}
            emptyMessage="This folder is empty."
            onBreadcrumbClick={searchFolderBrowser.navigateTo}
            onFolderClick={searchFolderBrowser.navigateInto}
            onFileClick={(item) => void openFileReadOnly(searchFolderPath, item)}
            extraCrumbs={[{ label: "Search results", onClick: () => setSearchFolder(null) }]}
          />
        )}

        {graphToken && !searchFolder && isSearching && (
          <KnowledgeSearchResults
            results={byTag(search.results, activeTag)}
            sites={activeTag ? [] : matchedSites}
            loading={search.loading}
            error={search.error}
            onResultClick={openSearchResult}
            onSiteClick={openSiteResult}
          />
        )}

        {graphToken && !searchFolder && !isSearching && selectedSite && (
          <FolderBrowser
            // While a level is loading, keep the list empty so the previous
            // folder's items don't linger under the "Loading…" indicator.
            items={browser.loading ? [] : byTag(browser.items, activeTag)}
            loading={browser.loading}
            error={browser.error}
            path={browser.path}
            emptyMessage="This library is empty."
            onBreadcrumbClick={browser.navigateTo}
            onFolderClick={browser.navigateInto}
            onFileClick={(item) => void openFileReadOnly(drivePath, item)}
            extraCrumbs={[{ label: "Knowledge Base", onClick: () => setSelectedSite(null) }]}
          />
        )}

        {graphToken && !searchFolder && !isSearching && !selectedSite && (
          <div className={styles.siteList}>
            {sitesQuery.error && <div className={styles.error}>{sitesQuery.error}</div>}
            {sitesQuery.loading && <div className={styles.empty}>Loading site collections…</div>}
            {!sitesQuery.loading && !sitesQuery.error && filteredSites.length === 0 && (
              <div className={styles.empty}>No SharePoint site collections found.</div>
            )}

            {filteredSites.map((site) => {
              const name = siteName(site);
              const desc = site.description ?? "";
              return (
                <button
                  key={site.id}
                  type="button"
                  className={styles.site}
                  onClick={() => setSelectedSite(site)}
                >
                  <div className={styles.siteIcon}>{siteInitials(name)}</div>
                  <div className={styles.siteBody}>
                    <div className={styles.siteName}>{name}</div>
                    {desc && <div className={styles.siteDesc}>{desc}</div>}
                  </div>
                  <span className={styles.chev} aria-hidden="true">›</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
